import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LabelsView } from '@wodi/shared';
import { Button, LoadingBlock, Notice, PrintLines, printSheet, PrintSheet } from '@wodi/ui';
import { useEffect, useRef } from 'react';

import { dispensingKeys, labelsQuery, markLabelsPrinted } from '../api/dispensing.ts';
import { errorText } from '../stock/common.tsx';
import styles from './pharmacy.module.css';

type Label = LabelsView['labels'][number];

/** One dispensing label (plan §9.1). Never truncated: long text goes on the 80 mm roll. */
function LabelSheet({
  label,
  sheetRef,
}: {
  label: Label;
  sheetRef: (el: HTMLElement | null) => void;
}) {
  return (
    <PrintSheet paper={label.paper} label={`Label for ${label.medicine}`} ref={sheetRef}>
      <PrintLines
        items={[
          {
            label: label.printCount > 0 ? `Copy ${String(label.printCount + 1)}` : 'Label',
            value: label.facilityLine,
          },
          { label: 'Patient', value: label.patientName },
          { label: 'Patient no.', value: label.patientNumber, identifier: true },
          { label: 'Medicine', value: `${label.medicine}, ${label.quantityLine}` },
          { label: 'How to take', value: label.instructions },
          ...(label.pharmacistNote === null
            ? []
            : [{ label: 'Note', value: label.pharmacistNote }]),
          { label: 'Dispensed', value: label.dispensedOn },
          { label: 'Dispense no.', value: label.dispenseNumber, identifier: true },
        ]}
      />
    </PrintSheet>
  );
}

/**
 * Loads a dispense's labels and prints them, recording each print (audited; later prints
 * say "Copy"). With `auto`, they print as soon as they are ready.
 */
export function DispenseLabels({ dispenseId, auto }: { dispenseId: string; auto: boolean }) {
  const queryClient = useQueryClient();
  const labels = useQuery(labelsQuery(dispenseId));
  const sheets = useRef(new Map<string, HTMLElement>());
  const printed = useMutation({
    mutationFn: (lineIds: string[]) => markLabelsPrinted(dispenseId, lineIds),
    onSuccess: (data) => {
      queryClient.setQueryData(dispensingKeys.labels(dispenseId), data);
    },
  });
  const started = useRef(false);
  const printAll = (list: readonly Label[]) => {
    for (const l of list) {
      const el = sheets.current.get(l.lineId);
      if (el !== undefined) printSheet(el);
    }
    if (list.length > 0) printed.mutate(list.map((l) => l.lineId));
  };
  useEffect(() => {
    if (auto && labels.isSuccess && !started.current) {
      started.current = true;
      printAll(labels.data.labels);
    }
  });
  if (labels.isPending) return <LoadingBlock label="Loading labels" rows={2} />;
  if (labels.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void labels.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(labels.error, "The labels couldn't be loaded. Try again.")}
      </Notice>
    );
  if (labels.data.labels.length === 0) return null;
  return (
    <div className={styles.section}>
      <div>
        <Button
          onPress={() => {
            printAll(labels.data.labels);
          }}
          isPending={printed.isPending}
          pendingLabel="Printing"
        >
          Print labels again
        </Button>
      </div>
      {labels.data.labels.map((l) => (
        <LabelSheet
          key={l.lineId}
          label={l}
          sheetRef={(el) => {
            if (el === null) sheets.current.delete(l.lineId);
            else sheets.current.set(l.lineId, el);
          }}
        />
      ))}
    </div>
  );
}
